import React, { useState, useEffect, useMemo } from 'react';
import { Socket } from 'socket.io-client';
import { MarketPriceChart } from './MarketPriceChart';

// ============ TYPES ============

interface MarketOrder {
  id: string;
  characterId: string;
  itemId: string;
  orderType: 'buy' | 'sell';
  quantity: number;
  pricePerUnit: number;
  totalValue: number;
  filledQuantity: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
  fulfilledAt?: string;
  claimed?: boolean;
}

interface PricePoint {
  timestamp: string;
  price: number;
  volume: number;
}

interface MarketSummary {
  itemId: string;
  region: string;
  lowestSellPrice: number | null;
  highestBuyPrice: number | null;
  lastSalePrice: number | null;
  dailyVolume: number;
  weeklyVolume: number;
  avgPrice7d: number | null;
  activeBuyOrders: number;
  activeSellOrders: number;
}

interface RegionalPrice {
  region: string;
  lowestSellPrice: number | null;
  highestBuyPrice: number | null;
  lastSalePrice: number | null;
}

// Region display info
const REGION_INFO: Record<string, { name: string; modifier: number; color: string }> = {
  'reaches': { name: 'The Reaches', modifier: 1.00, color: '#10b981' },
  'paradise': { name: 'Paradise Islands', modifier: 1.05, color: '#ec4899' },
  'arcadia': { name: 'Arcadia', modifier: 0.85, color: '#8b5cf6' },
};

interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: string;
  rarity: string;
  icon: string;
  tradeable?: boolean;
}

interface InventoryItem {
  itemId: string;
  quantity: number;
  definition: ItemDefinition | null;
}

interface MarketplaceExchangePanelProps {
  socket: Socket | null;
  inventory: InventoryItem[];
  pieces: number;
  onPiecesChange?: (amount: number) => void;
}

// ============ CONSTANTS ============

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

// Helper to resolve icon paths and provide fallback
const getIconPath = (icon: string | undefined): string => {
  const publicUrl = process.env.PUBLIC_URL || '';
  
  if (!icon) {
    return `${publicUrl}/assets/unknown.png`;
  }
  
  // If already a full URL, use as-is
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return icon;
  }
  
  // If starts with /assets, prepend publicUrl
  if (icon.startsWith('/assets')) {
    return `${publicUrl}${icon}`;
  }
  
  // Otherwise assume it's a relative path and prepend /assets/
  return `${publicUrl}/assets/${icon}`;
};

// ============ COMPONENT ============

export const MarketplaceExchangePanel: React.FC<MarketplaceExchangePanelProps> = ({
  socket,
  inventory,
  pieces,
  onPiecesChange,
}) => {
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [orderType, setOrderType] = useState<'buy' | 'sell'>('buy');
  const [quantity, setQuantity] = useState<number>(1);
  const [pricePerUnit, setPricePerUnit] = useState<number>(100);
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  
  // Market data
  const [buyOrders, setBuyOrders] = useState<MarketOrder[]>([]);
  const [sellOrders, setSellOrders] = useState<MarketOrder[]>([]);
  const [priceHistory, setPriceHistory] = useState<PricePoint[]>([]);
  const [marketSummary, setMarketSummary] = useState<MarketSummary | null>(null);
  const [myOrders, setMyOrders] = useState<MarketOrder[]>([]);
  const [regionalPrices, setRegionalPrices] = useState<RegionalPrice[]>([]);
  const [showRegionalPrices, setShowRegionalPrices] = useState<boolean>(false);
  const [showOrderHistory, setShowOrderHistory] = useState<boolean>(false);
  
  // All items in the game - initialize with common items as fallback
  const [allGameItems, setAllGameItems] = useState<Array<{ id: string; name: string; type: string; rarity: string; icon: string; tradeable: boolean }>>([
    // Weapons
    { id: 'rusty_cutlass', name: 'Rusty Cutlass', type: 'weapon', rarity: 'common', icon: '/assets/items/weapons/weapon_rustycutlass.png', tradeable: true },
    { id: 'steel_saber', name: 'Steel Saber', type: 'weapon', rarity: 'uncommon', icon: '/assets/items/weapons/weapon_steelsaber.png', tradeable: true },
    { id: 'navy_sword', name: 'Navy Sword', type: 'weapon', rarity: 'uncommon', icon: '/assets/items/weapons/weapon_marinesword.png', tradeable: true },
    { id: 'pirate_blade', name: 'Pirate Blade', type: 'weapon', rarity: 'rare', icon: '/assets/items/weapons/weapon_pirateblade.png', tradeable: true },
    { id: 'wado_ichimonji', name: 'Wado Ichimonji', type: 'weapon', rarity: 'legendary', icon: '/assets/items/weapons/weapon_wadoichimonji.png', tradeable: true },
    // Armor
    { id: 'leather_vest', name: 'Leather Vest', type: 'armor', rarity: 'common', icon: '/assets/items/armor/armor_leathervest.png', tradeable: true },
    { id: 'chain_mail', name: 'Chain Mail', type: 'armor', rarity: 'uncommon', icon: '/assets/items/armor/armor_chainmail.png', tradeable: true },
    { id: 'navy_coat', name: 'Navy Coat', type: 'armor', rarity: 'rare', icon: '/assets/items/armor/armor_marinecoat.png', tradeable: true },
    // Accessories
    { id: 'lucky_charm', name: 'Lucky Charm', type: 'accessory', rarity: 'common', icon: '/assets/items/accessories/accessory_luckycharm.png', tradeable: true },
    { id: 'compass', name: 'Navigator\'s Compass', type: 'accessory', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_compass.png', tradeable: true },
    { id: 'spyglass', name: 'Spyglass', type: 'accessory', rarity: 'rare', icon: '/assets/items/consumables/consumable_spyglass.png', tradeable: true },
    // Consumables
    { id: 'health_potion', name: 'Health Potion', type: 'consumable', rarity: 'common', icon: '/assets/items/consumables/consumable_healthpotion.png', tradeable: true },
    { id: 'repair_kit', name: 'Repair Kit', type: 'consumable', rarity: 'common', icon: '/assets/items/consumables/consumable_repairkit.png', tradeable: true },
    { id: 'speed_boost', name: 'Speed Boost', type: 'consumable', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_speedboost.png', tradeable: true },
    { id: 'damage_boost', name: 'Damage Boost', type: 'consumable', rarity: 'rare', icon: '/assets/items/consumables/consumable_damageboost.png', tradeable: true },
    { id: 'large_health_potion', name: 'Large Health Potion', type: 'consumable', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_largehealthpotion.png', tradeable: true },
    { id: 'mega_repair_kit', name: 'Mega Repair Kit', type: 'consumable', rarity: 'rare', icon: '/assets/items/consumables/consumable_megarepairkit.png', tradeable: true },
    { id: 'sail_patch', name: 'Sail Patch', type: 'consumable', rarity: 'common', icon: '/assets/items/consumables/consumable_sailpatch.png', tradeable: true },
    { id: 'cannon_powder', name: 'Cannon Powder', type: 'consumable', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_cannonpowder.png', tradeable: true },
    { id: 'defense_tonic', name: 'Defense Tonic', type: 'consumable', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_defensetonic.png', tradeable: true },
    { id: 'escape_smoke', name: 'Escape Smoke', type: 'consumable', rarity: 'rare', icon: '/assets/items/consumables/consumable_escapesmoke.png', tradeable: true },
    { id: 'treasure_map', name: 'Treasure Map', type: 'consumable', rarity: 'epic', icon: '/assets/items/consumables/consumable_treasuremap.png', tradeable: true },
    { id: 'scout_flare', name: 'Scout Flare', type: 'consumable', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_scoutflare.png', tradeable: true },
    { id: 'speed_tonic', name: 'Speed Tonic', type: 'consumable', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_speedtonic.png', tradeable: true },
    { id: 'fishing_lure', name: 'Premium Fishing Lure', type: 'consumable', rarity: 'uncommon', icon: '/assets/items/consumables/consumable_fishinglure.png', tradeable: true },
    { id: 'anchor_chain', name: 'Reinforced Anchor', type: 'consumable', rarity: 'rare', icon: '/assets/items/consumables/consumable_anchorchain.png', tradeable: true },
    { id: 'distress_flare', name: 'Distress Flare', type: 'consumable', rarity: 'rare', icon: '/assets/items/consumables/consumable_distressflare.png', tradeable: true },
    { id: 'xp_scroll', name: 'Experience Scroll', type: 'consumable', rarity: 'rare', icon: '/assets/items/consumables/consumable_xpscroll.png', tradeable: true },
    { id: 'berry_charm', name: 'Merchant\'s Charm', type: 'consumable', rarity: 'rare', icon: '/assets/items/consumables/consumable_berrycharm.png', tradeable: true },
    // Materials
    { id: 'wood_plank', name: 'Wood Plank', type: 'material', rarity: 'common', icon: '/assets/items/materials/material_woodplank.png', tradeable: true },
    { id: 'iron_ore', name: 'Iron Ore', type: 'material', rarity: 'common', icon: '/assets/items/materials/material_ironore.png', tradeable: true },
    { id: 'cloth', name: 'Cloth', type: 'material', rarity: 'common', icon: '/assets/items/materials/material_cloth.png', tradeable: true },
    { id: 'gunpowder', name: 'Gunpowder', type: 'material', rarity: 'uncommon', icon: '/assets/items/materials/material_gunpowder.png', tradeable: true },
    { id: 'sea_stone', name: 'Sea Stone', type: 'material', rarity: 'epic', icon: '/assets/items/consumables/consumable_seastone.png', tradeable: true },
    { id: 'sardine', name: 'Sardine', type: 'material', rarity: 'common', icon: '/assets/items/fish/fish_sardines.png', tradeable: true },
    { id: 'mackerel', name: 'Mackerel', type: 'material', rarity: 'common', icon: '/assets/items/fish/fish_mackerel.png', tradeable: true },
    { id: 'sea_bass', name: 'Sea Bass', type: 'material', rarity: 'common', icon: '/assets/items/fish/fish_seabass.png', tradeable: true },
    { id: 'squid', name: 'Squid', type: 'material', rarity: 'common', icon: '/assets/items/fish/fish_squid.png', tradeable: true },
    { id: 'tuna', name: 'Bluefin Tuna', type: 'material', rarity: 'uncommon', icon: '/assets/items/fish/fish_tuna.png', tradeable: true },
    { id: 'swordfish', name: 'Swordfish', type: 'material', rarity: 'uncommon', icon: '/assets/items/fish/fish_swordfish.png', tradeable: true },
    { id: 'tropical_fish', name: 'Tropical Fish', type: 'material', rarity: 'uncommon', icon: '/assets/items/fish/fish_tropical.png', tradeable: true },
    { id: 'octopus', name: 'Octopus', type: 'material', rarity: 'uncommon', icon: '/assets/items/fish/fish_octopus.png', tradeable: true },
  ]);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Filtered inventory for item selection
  // For Exchange tab, show all items in the game
  const filteredInventory = useMemo(() => {
    // Convert all game items to inventory format
    const itemsToShow = allGameItems.map(item => ({
      itemId: item.id,
      quantity: inventory.find(i => i.itemId === item.id)?.quantity || 0,
      definition: inventory.find(i => i.itemId === item.id)?.definition || {
        id: item.id,
        name: item.name,
        description: '',
        type: item.type as any,
        rarity: item.rarity as any,
        icon: item.icon,
        tradeable: item.tradeable,
        stackable: true,
        maxStack: 99,
        buyPrice: 0,
        sellPrice: 0,
      },
    }));
    
    if (!searchQuery.trim()) return itemsToShow;
    
    const q = searchQuery.toLowerCase();
    return itemsToShow.filter(item => {
      const name = item.definition?.name || item.itemId;
      const description = item.definition?.description || '';
      return name.toLowerCase().includes(q) || description.toLowerCase().includes(q);
    });
  }, [allGameItems, inventory, searchQuery]);

  // Socket listeners
  useEffect(() => {
    if (!socket) return;

    const handleOrderPlaced = (data: { order: MarketOrder; message: string }) => {
      setMessage(data.message);
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
      
      // Refresh orders and my orders
      if (selectedItem) {
        socket.emit('marketplace:getOrders', { itemId: selectedItem.itemId, orderType: 'buy' });
        socket.emit('marketplace:getOrders', { itemId: selectedItem.itemId, orderType: 'sell' });
      }
      socket.emit('marketplace:getMyOrders');
    };

    const handleOrderFilled = (data: { orderId: string; quantity: number; message: string }) => {
      setMessage(data.message);
      setMessageType('success');
      setTimeout(() => setMessage(''), 5000);
      
      // Refresh orders
      if (selectedItem) {
        socket.emit('marketplace:getOrders', { itemId: selectedItem.itemId, orderType: 'buy' });
        socket.emit('marketplace:getOrders', { itemId: selectedItem.itemId, orderType: 'sell' });
        socket.emit('marketplace:getMarketSummary', { itemId: selectedItem.itemId });
      }
      socket.emit('marketplace:getMyOrders');
    };

    const handleOrderCancelled = (data: { orderId: string; message: string }) => {
      setMessage(data.message);
      setMessageType('success');
      setTimeout(() => setMessage(''), 3000);
      socket.emit('marketplace:getMyOrders');
    };

    const handleOrders = (data: { itemId: string; orderType: 'buy' | 'sell'; orders: MarketOrder[] }) => {
      if (data.orderType === 'buy') {
        setBuyOrders(data.orders);
      } else {
        setSellOrders(data.orders);
      }
    };

    const handleMyOrders = (data: { orders: MarketOrder[] }) => {
      setMyOrders(data.orders);
    };

    const handlePriceHistory = (data: { itemId: string; history: PricePoint[] }) => {
      setPriceHistory(data.history);
    };

    const handleMarketSummary = (data: { itemId: string; summary: MarketSummary }) => {
      setMarketSummary(data.summary);
    };

    const handleRegionalPrices = (data: { itemId: string; prices: RegionalPrice[] }) => {
      setRegionalPrices(data.prices);
    };

    const handleAllItems = (data: { items: Array<{ id: string; name: string; type: string; rarity: string; icon: string; tradeable: boolean }> }) => {
      console.log('Received all items from server:', data.items.length, 'items');
      setAllGameItems(data.items);
    };

    const handleError = (data: { message: string }) => {
      setMessage(data.message);
      setMessageType('error');
      setTimeout(() => setMessage(''), 3000);
    };

    socket.on('marketplace:orderPlaced', handleOrderPlaced);
    socket.on('marketplace:orderFilled', handleOrderFilled);
    socket.on('marketplace:orderCancelled', handleOrderCancelled);
    socket.on('marketplace:orders', handleOrders);
    socket.on('marketplace:myOrders', handleMyOrders);
    socket.on('marketplace:priceHistory', handlePriceHistory);
    socket.on('marketplace:marketSummary', handleMarketSummary);
    socket.on('marketplace:regionalPrices', handleRegionalPrices);
    socket.on('marketplace:allItems', handleAllItems);
    socket.on('marketplace:error', handleError);

    return () => {
      socket.off('marketplace:orderPlaced', handleOrderPlaced);
      socket.off('marketplace:orderFilled', handleOrderFilled);
      socket.off('marketplace:orderCancelled', handleOrderCancelled);
      socket.off('marketplace:orders', handleOrders);
      socket.off('marketplace:myOrders', handleMyOrders);
      socket.off('marketplace:priceHistory', handlePriceHistory);
      socket.off('marketplace:marketSummary', handleMarketSummary);
      socket.off('marketplace:regionalPrices', handleRegionalPrices);
      socket.off('marketplace:allItems', handleAllItems);
      socket.off('marketplace:error', handleError);
    };
  }, [socket, selectedItem]);

  // Request inventory data on component mount
  useEffect(() => {
    if (socket && inventory.length === 0) {
      socket.emit('inventory:get');
    }
  }, [socket, inventory.length]);

  // Load my orders and all items on mount
  useEffect(() => {
    if (socket) {
      console.log('Emitting marketplace:getAllItems');
      socket.emit('marketplace:getMyOrders');
      socket.emit('marketplace:getAllItems');
      
      // Retry after 2 seconds if items haven't loaded
      const retryTimer = setTimeout(() => {
        if (allGameItems.length === 0) {
          console.log('Retrying marketplace:getAllItems');
          socket.emit('marketplace:getAllItems');
        }
      }, 2000);
      
      return () => clearTimeout(retryTimer);
    }
  }, [socket, allGameItems.length]);

  // Load market data when item selected
  useEffect(() => {
    if (socket && selectedItem) {
      socket.emit('marketplace:getOrders', { itemId: selectedItem.itemId, orderType: 'buy' });
      socket.emit('marketplace:getOrders', { itemId: selectedItem.itemId, orderType: 'sell' });
      socket.emit('marketplace:getPriceHistory', { itemId: selectedItem.itemId, days: 7 });
      socket.emit('marketplace:getMarketSummary', { itemId: selectedItem.itemId });
      socket.emit('marketplace:getRegionalPrices', { itemId: selectedItem.itemId });
      
      // Set suggested price based on market
      if (orderType === 'buy' && sellOrders.length > 0) {
        setPricePerUnit(sellOrders[0].pricePerUnit);
      } else if (orderType === 'sell' && buyOrders.length > 0) {
        setPricePerUnit(buyOrders[0].pricePerUnit);
      }
    }
  }, [selectedItem, socket, orderType, buyOrders, sellOrders]);

  // Auto-select first item when items load
  useEffect(() => {
    if (!selectedItem && filteredInventory.length > 0) {
      setSelectedItem(filteredInventory[0]);
    }
  }, [filteredInventory, selectedItem]);

  const handlePlaceOrder = () => {
    if (!socket || !selectedItem) return;

    const totalCost = quantity * pricePerUnit;

    if (orderType === 'buy') {
      if (pieces < totalCost) {
        setMessage(`Insufficient funds. Need ₱${totalCost.toLocaleString()}`);
        setMessageType('error');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      socket.emit('marketplace:placeBuyOrder', {
        itemId: selectedItem.itemId,
        quantity,
        pricePerUnit,
      });
    } else {
      if (selectedItem.quantity < quantity) {
        setMessage(`Insufficient items. Have ${selectedItem.quantity}, need ${quantity}`);
        setMessageType('error');
        setTimeout(() => setMessage(''), 3000);
        return;
      }
      socket.emit('marketplace:placeSellOrder', {
        itemId: selectedItem.itemId,
        quantity,
        pricePerUnit,
      });
    }
  };

  const handleCancelOrder = (orderId: string) => {
    if (!socket) return;
    socket.emit('marketplace:cancelOrder', { orderId });
  };

  const handleClaimOrder = (order: MarketOrder) => {
    if (!socket) return;
    socket.emit('marketplace:claimOrder', { orderId: order.id, orderType: order.orderType });
  };

  const totalValue = quantity * pricePerUnit;
  const marketplaceFee = orderType === 'sell' ? Math.floor(totalValue * 0.02) : 0;
  const netReceive = orderType === 'sell' ? totalValue - marketplaceFee : 0;

  const messageHeight = message ? 60 : 0;

  return (
    <div style={styles.container}>
      {/* Message */}
      {message && (
        <div
          style={{
            ...styles.message,
            background: messageType === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            borderColor: messageType === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)',
            color: messageType === 'success' ? '#22c55e' : '#ef4444',
          }}
        >
          {message}
        </div>
      )}

      <div style={{ ...styles.content, top: messageHeight }}>
        {/* Left: Item Selection */}
        <div style={styles.sidebar}>
          <div style={styles.searchBox}>
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <div style={styles.itemList} className="exchange-scrollbar">
            {filteredInventory.length > 0 ? (
              filteredInventory.map((item) => (
                <div
                  key={item.itemId}
                  style={{
                    ...styles.itemRow,
                    background: selectedItem?.itemId === item.itemId ? 'rgba(88, 166, 255, 0.15)' : '#0f172a',
                    borderLeft: `3px solid ${item.definition ? RARITY_COLORS[item.definition.rarity] : '#666'}`,
                  }}
                  onClick={() => setSelectedItem(item)}
                >
                  <div style={styles.itemIcon}>
                    <img 
                      src={getIconPath(item.definition?.icon)} 
                      alt={item.definition?.name || 'Item'} 
                      style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const publicUrl = process.env.PUBLIC_URL || '';
                        target.src = `${publicUrl}/assets/unknown.png`;
                      }}
                    />
                  </div>
                  <div style={styles.itemInfo}>
                    <div style={{ color: item.definition ? RARITY_COLORS[item.definition.rarity] : '#fff', fontSize: 12 }}>
                      {item.definition?.name || 'Unknown Item'}
                    </div>
                    <div style={{ fontSize: 10, color: '#6e7681' }}>Qty: {item.quantity}</div>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '16px 12px', color: '#6e7681', fontSize: 11, textAlign: 'center' }}>
                No items available
              </div>
            )}
          </div>
        </div>

        {/* Center: Order Book & Chart */}
        <div style={styles.centerPanel} className="exchange-scrollbar">
          {selectedItem ? (
            <>
              <div style={styles.itemHeader}>
                <div style={styles.itemHeaderIcon}>
                  <img 
                    src={getIconPath(selectedItem.definition?.icon)} 
                    alt={selectedItem.definition?.name || 'Item'} 
                    style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const publicUrl = process.env.PUBLIC_URL || '';
                      target.src = `${publicUrl}/assets/unknown.png`;
                    }}
                  />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: selectedItem.definition ? RARITY_COLORS[selectedItem.definition.rarity] : '#fff', fontFamily: 'monospace', fontSize: 16 }}>
                    {selectedItem.definition?.name || selectedItem.itemId}
                  </h3>
                  <p style={{ margin: 0, fontSize: 11, color: '#6e7681', fontFamily: 'monospace' }}>
                    {selectedItem.definition?.description || ''}
                  </p>
                </div>
              </div>

              {/* Market Summary */}
              {marketSummary && (
                <div style={styles.summaryBox}>
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>LAST SALE</span>
                    <span style={styles.summaryValue}>₱{marketSummary.lastSalePrice?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>7D AVG</span>
                    <span style={styles.summaryValue}>₱{marketSummary.avgPrice7d?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div style={styles.summaryRow}>
                    <span style={styles.summaryLabel}>DAILY VOL</span>
                    <span style={styles.summaryValue}>{marketSummary.dailyVolume}</span>
                  </div>
                </div>
              )}

              {/* Regional Prices Toggle */}
              <button
                onClick={() => setShowRegionalPrices(!showRegionalPrices)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  marginBottom: 8,
                  background: showRegionalPrices ? '#1e40af' : '#21262d',
                  border: '1px solid ' + (showRegionalPrices ? '#3b82f6' : '#30363d'),
                  borderRadius: 0,
                  color: showRegionalPrices ? '#bfdbfe' : '#8b949e',
                  fontFamily: 'monospace',
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>REGIONAL PRICES</span>
                <span>{showRegionalPrices ? '▼' : '▶'}</span>
              </button>

              {/* Regional Prices Panel */}
              {showRegionalPrices && regionalPrices.length > 0 && (
                <div style={{
                  marginBottom: 12,
                  background: '#161b22',
                  border: '1px solid #30363d',
                  borderRadius: 0,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '8px 12px',
                    background: '#21262d',
                    borderBottom: '1px solid #30363d',
                    fontSize: 10,
                    fontFamily: 'monospace',
                    color: '#8b949e',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span>REGION</span>
                    <span>SELL</span>
                    <span>BUY</span>
                    <span>ARBITRAGE</span>
                  </div>
                  {regionalPrices.map((rp) => {
                    const regionInfo = REGION_INFO[rp.region] || { name: rp.region, modifier: 1, color: '#6e7681' };
                    const minSellAcrossRegions = Math.min(
                      ...regionalPrices.filter(r => r.lowestSellPrice).map(r => r.lowestSellPrice!)
                    );
                    const arbitrageProfit = rp.highestBuyPrice && minSellAcrossRegions && minSellAcrossRegions < rp.highestBuyPrice
                      ? rp.highestBuyPrice - minSellAcrossRegions
                      : null;
                    
                    return (
                      <div
                        key={rp.region}
                        style={{
                          padding: '6px 12px',
                          borderBottom: '1px solid #21262d',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: 11,
                          fontFamily: 'monospace',
                        }}
                      >
                        <span style={{ color: regionInfo.color, minWidth: 80 }}>
                          {regionInfo.name}
                          <span style={{ color: '#6e7681', fontSize: 9, marginLeft: 4 }}>
                            {regionInfo.modifier > 1 ? `+${((regionInfo.modifier - 1) * 100).toFixed(0)}%` : 
                             regionInfo.modifier < 1 ? `-${((1 - regionInfo.modifier) * 100).toFixed(0)}%` : ''}
                          </span>
                        </span>
                        <span style={{ color: rp.lowestSellPrice ? '#ef4444' : '#6e7681', minWidth: 60, textAlign: 'right' }}>
                          {rp.lowestSellPrice ? `₱${rp.lowestSellPrice.toLocaleString()}` : '-'}
                        </span>
                        <span style={{ color: rp.highestBuyPrice ? '#22c55e' : '#6e7681', minWidth: 60, textAlign: 'right' }}>
                          {rp.highestBuyPrice ? `₱${rp.highestBuyPrice.toLocaleString()}` : '-'}
                        </span>
                        <span style={{ 
                          color: arbitrageProfit && arbitrageProfit > 0 ? '#fbbf24' : '#6e7681',
                          minWidth: 60,
                          textAlign: 'right',
                          fontWeight: arbitrageProfit && arbitrageProfit > 0 ? 'bold' : 'normal',
                        }}>
                          {arbitrageProfit && arbitrageProfit > 0 ? `+₱${arbitrageProfit.toLocaleString()}` : '-'}
                        </span>
                      </div>
                    );
                  })}
                  
                  {/* Arbitrage Tip */}
                  {(() => {
                    const sellPrices = regionalPrices.filter(r => r.lowestSellPrice);
                    const buyPrices = regionalPrices.filter(r => r.highestBuyPrice);
                    if (sellPrices.length > 0 && buyPrices.length > 0) {
                      const cheapest = sellPrices.reduce((a, b) => 
                        (a.lowestSellPrice || Infinity) < (b.lowestSellPrice || Infinity) ? a : b
                      );
                      const highest = buyPrices.reduce((a, b) => 
                        (a.highestBuyPrice || 0) > (b.highestBuyPrice || 0) ? a : b
                      );
                      if (cheapest.lowestSellPrice && highest.highestBuyPrice && 
                          cheapest.region !== highest.region &&
                          highest.highestBuyPrice > cheapest.lowestSellPrice) {
                        const profit = highest.highestBuyPrice - cheapest.lowestSellPrice;
                        const profitPct = ((profit / cheapest.lowestSellPrice) * 100).toFixed(1);
                        return (
                          <div style={{
                            padding: '8px 12px',
                            background: 'linear-gradient(90deg, #1e3a1e 0%, #1e1e3a 100%)',
                            borderTop: '1px solid #30363d',
                            fontSize: 10,
                            fontFamily: 'monospace',
                            color: '#fbbf24',
                          }}>
                            TIP: Buy in <span style={{ color: REGION_INFO[cheapest.region]?.color || '#fff' }}>
                              {REGION_INFO[cheapest.region]?.name || cheapest.region}
                            </span>, sell in <span style={{ color: REGION_INFO[highest.region]?.color || '#fff' }}>
                              {REGION_INFO[highest.region]?.name || highest.region}
                            </span> for <span style={{ color: '#22c55e' }}>+{profitPct}% profit</span>
                          </div>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* Price Chart */}
              <div style={{ marginBottom: 16 }}>
                <MarketPriceChart
                  data={priceHistory.map(p => ({
                    timestamp: new Date(p.timestamp),
                    price: p.price,
                    volume: p.volume,
                  }))}
                  width={500}
                  height={200}
                />
              </div>

              {/* Order Book */}
              <div style={styles.orderBook}>
                <div style={styles.orderBookSide}>
                  <h4 style={styles.orderBookTitle}>BUY ORDERS ({buyOrders.length})</h4>
                  <div style={styles.orderBookList}>
                    {buyOrders.slice(0, 5).map((order) => (
                      <div 
                        key={order.id} 
                        style={{
                          ...styles.orderBookRow,
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                        }}
                        onClick={() => {
                          setOrderType('sell');
                          setPricePerUnit(order.pricePerUnit);
                          setQuantity(Math.min(order.quantity - order.filledQuantity, selectedItem?.quantity || 1));
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <span style={{ color: '#22c55e' }}>₱{order.pricePerUnit}</span>
                          <span style={{ color: '#6e7681', marginLeft: 8 }}>x{order.quantity - order.filledQuantity}</span>
                        </div>
                        <span style={{ color: '#6e7681', fontSize: 9 }}>
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                    {buyOrders.length === 0 && (
                      <div style={{ color: '#6e7681', fontSize: 11, textAlign: 'center', padding: 16 }}>
                        No buy orders
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.orderBookSide}>
                  <h4 style={styles.orderBookTitle}>SELL ORDERS ({sellOrders.length})</h4>
                  <div style={styles.orderBookList}>
                    {sellOrders.slice(0, 5).map((order) => (
                      <div 
                        key={order.id} 
                        style={{
                          ...styles.orderBookRow,
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                        }}
                        onClick={() => {
                          setOrderType('buy');
                          setPricePerUnit(order.pricePerUnit);
                          setQuantity(Math.min(order.quantity - order.filledQuantity, 10));
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <span style={{ color: '#ef4444' }}>₱{order.pricePerUnit}</span>
                          <span style={{ color: '#6e7681', marginLeft: 8 }}>x{order.quantity - order.filledQuantity}</span>
                        </div>
                        <span style={{ color: '#6e7681', fontSize: 9 }}>
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                    {sellOrders.length === 0 && (
                      <div style={{ color: '#6e7681', fontSize: 11, textAlign: 'center', padding: 16 }}>
                        No sell orders
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={styles.noSelection}>
              <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#6e7681' }}>
                Select an item to view market data
              </span>
            </div>
          )}
        </div>

        {/* Right: Place Order & My Orders */}
        <div style={styles.rightPanel}>
          {selectedItem ? (
            <>
              <div style={styles.placeOrderBox}>
                <h4 style={styles.sectionTitle}>PLACE ORDER</h4>
                
                {/* Order Type */}
                <div style={styles.orderTypeButtons}>
                  <button
                    style={{
                      ...styles.orderTypeBtn,
                      ...(orderType === 'buy' ? styles.orderTypeBtnActive : {}),
                    }}
                    onClick={() => setOrderType('buy')}
                  >
                    BUY
                  </button>
                  <button
                    style={{
                      ...styles.orderTypeBtn,
                      ...(orderType === 'sell' ? styles.orderTypeBtnActive : {}),
                    }}
                    onClick={() => setOrderType('sell')}
                  >
                    SELL
                  </button>
                </div>

                {/* Quantity */}
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>QUANTITY</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input
                      type="number"
                      min={1}
                      max={orderType === 'sell' ? selectedItem.quantity : 1000}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      style={{
                        width: 32,
                        height: 32,
                        padding: 0,
                        background: '#1f2937',
                        border: '1px solid #30363d',
                        color: '#e6edf3',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 700,
                        borderRadius: 0,
                      }}
                      title="Decrease quantity"
                    >
                      −
                    </button>
                    <button
                      onClick={() => setQuantity(Math.min(orderType === 'sell' ? selectedItem.quantity : 1000, quantity + 1))}
                      style={{
                        width: 32,
                        height: 32,
                        padding: 0,
                        background: '#1f2937',
                        border: '1px solid #30363d',
                        color: '#e6edf3',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 700,
                        borderRadius: 0,
                      }}
                      title="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Price Per Unit */}
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>PRICE PER UNIT</label>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input
                      type="number"
                      min={1}
                      value={pricePerUnit}
                      onChange={(e) => setPricePerUnit(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <button
                      onClick={() => setPricePerUnit(Math.max(1, pricePerUnit - 1))}
                      style={{
                        width: 32,
                        height: 32,
                        padding: 0,
                        background: '#1f2937',
                        border: '1px solid #30363d',
                        color: '#e6edf3',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 700,
                        borderRadius: 0,
                      }}
                      title="Decrease price"
                    >
                      −
                    </button>
                    <button
                      onClick={() => setPricePerUnit(pricePerUnit + 1)}
                      style={{
                        width: 32,
                        height: 32,
                        padding: 0,
                        background: '#1f2937',
                        border: '1px solid #30363d',
                        color: '#e6edf3',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 700,
                        borderRadius: 0,
                      }}
                      title="Increase price"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Total */}
                <div style={styles.totalBox}>
                  <div style={styles.totalRow}>
                    <span style={styles.totalLabel}>TOTAL</span>
                    <span style={styles.totalValue}>₱{totalValue.toLocaleString()}</span>
                  </div>
                  {orderType === 'sell' && (
                    <>
                      <div style={styles.totalRow}>
                        <span style={styles.totalLabel}>FEE (2%)</span>
                        <span style={{ fontSize: 12, color: '#ef4444', fontFamily: 'monospace' }}>
                          -₱{marketplaceFee.toLocaleString()}
                        </span>
                      </div>
                      <div style={styles.totalRow}>
                        <span style={styles.totalLabel}>YOU RECEIVE</span>
                        <span style={{ fontSize: 14, color: '#22c55e', fontFamily: 'monospace', fontWeight: 600 }}>
                          ₱{netReceive.toLocaleString()}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Place Order Button */}
                <button
                  style={{
                    ...styles.placeOrderBtn,
                    background: orderType === 'buy' ? '#238636' : '#1f6feb',
                  }}
                  onClick={handlePlaceOrder}
                >
                  {orderType === 'buy' ? 'PLACE BUY ORDER' : 'PLACE SELL ORDER'}
                </button>
              </div>

              {/* My Orders */}
              <div style={styles.myOrdersBox}>
                <h4 style={styles.sectionTitle}>MY ORDERS ({myOrders.filter(o => o.status === 'active').length})</h4>
                <div style={styles.myOrdersList}>
                  {myOrders.filter(o => o.status === 'active').slice(0, 5).map((order) => {
                    const item = inventory.find(i => i.itemId === order.itemId);
                    return (
                      <div key={order.id} style={styles.myOrderRow}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, color: order.orderType === 'buy' ? '#22c55e' : '#ef4444', marginBottom: 2 }}>
                            {order.orderType.toUpperCase()}
                          </div>
                          <div style={{ fontSize: 10, color: '#6e7681' }}>
                            {item?.definition?.name || order.itemId}
                          </div>
                          <div style={{ fontSize: 10, color: '#6e7681' }}>
                            {order.quantity - order.filledQuantity}x @ ₱{order.pricePerUnit}
                          </div>
                          <div style={{ fontSize: 9, color: '#6e7681', marginTop: 2 }}>
                            Created: {new Date(order.createdAt).toLocaleTimeString()}
                          </div>
                        </div>
                        <button
                          style={styles.cancelBtn}
                          onClick={() => handleCancelOrder(order.id)}
                        >
                          X
                        </button>
                      </div>
                    );
                  })}
                  {myOrders.filter(o => o.status === 'active').length === 0 && (
                    <div style={{ color: '#6e7681', fontSize: 11, textAlign: 'center', padding: 16 }}>
                      No active orders
                    </div>
                  )}
                </div>
              </div>

              {/* Order History Toggle */}
              <button
                onClick={() => setShowOrderHistory(!showOrderHistory)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  marginTop: 8,
                  background: showOrderHistory ? '#1e40af' : '#21262d',
                  border: '1px solid ' + (showOrderHistory ? '#3b82f6' : '#30363d'),
                  borderRadius: 0,
                  color: showOrderHistory ? '#bfdbfe' : '#8b949e',
                  fontFamily: 'monospace',
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>ORDER HISTORY</span>
                <span>{showOrderHistory ? '▼' : '▶'}</span>
              </button>

              {/* Order History Panel */}
              {showOrderHistory && (
                <div style={{
                  marginTop: 8,
                  background: '#161b22',
                  border: '1px solid #30363d',
                  borderRadius: 0,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '8px 12px',
                    background: '#21262d',
                    borderBottom: '1px solid #30363d',
                    fontSize: 10,
                    fontFamily: 'monospace',
                    color: '#8b949e',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}>
                    <span>FILLED/CANCELLED ORDERS</span>
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {myOrders.filter(o => o.status !== 'active').length > 0 ? (
                      myOrders.filter(o => o.status !== 'active').slice(0, 10).map((order) => {
                        const item = inventory.find(i => i.itemId === order.itemId);
                        const fillPercentage = order.quantity > 0 ? Math.round((order.filledQuantity / order.quantity) * 100) : 0;
                        const isFilled = order.status === 'filled' && order.filledQuantity > 0;
                        return (
                          <div
                            key={order.id}
                            style={{
                              padding: '8px 12px',
                              borderBottom: '1px solid #21262d',
                              fontSize: 10,
                              fontFamily: 'monospace',
                              color: '#8b949e',
                              background: order.claimed ? '#0d1b2a' : 'transparent',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                              <span style={{ color: order.orderType === 'buy' ? '#22c55e' : '#ef4444' }}>
                                {order.orderType.toUpperCase()}
                              </span>
                              <span style={{ color: order.status === 'filled' ? '#22c55e' : '#ef4444' }}>
                                {order.status.toUpperCase()}
                              </span>
                            </div>
                            <div style={{ color: '#6e7681', fontSize: 9, marginBottom: 2 }}>
                              {item?.definition?.name || order.itemId}
                            </div>
                            <div style={{ color: '#6e7681', fontSize: 9, marginBottom: 2 }}>
                              {order.filledQuantity}/{order.quantity}x @ ₱{order.pricePerUnit} ({fillPercentage}%)
                            </div>
                            <div style={{ color: '#6e7681', fontSize: 8, marginBottom: 2 }}>
                              Created: {new Date(order.createdAt).toLocaleTimeString()}
                            </div>
                            {order.fulfilledAt && (
                              <div style={{ color: '#22c55e', fontSize: 8, marginBottom: 2 }}>
                                Fulfilled: {new Date(order.fulfilledAt).toLocaleTimeString()}
                              </div>
                            )}
                            <div style={{ 
                              width: '100%', 
                              height: 4, 
                              background: '#21262d', 
                              borderRadius: 0,
                              overflow: 'hidden',
                              marginTop: 4,
                              marginBottom: isFilled ? 6 : 0
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${fillPercentage}%`,
                                background: order.orderType === 'buy' ? '#22c55e' : '#ef4444',
                                transition: 'width 0.3s'
                              }} />
                            </div>
                            {isFilled && !order.claimed && (
                              <button
                                style={{
                                  width: '100%',
                                  padding: '4px 8px',
                                  background: '#238636',
                                  border: '1px solid #2ea043',
                                  borderRadius: 2,
                                  color: '#fff',
                                  fontSize: 9,
                                  fontFamily: 'monospace',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                }}
                                onClick={() => handleClaimOrder(order)}
                              >
                                CLAIM {order.orderType === 'buy' ? 'ITEMS' : 'PIECES'}
                              </button>
                            )}
                            {order.claimed && (
                              <div style={{
                                width: '100%',
                                padding: '4px 8px',
                                background: '#0d3b1f',
                                border: '1px solid #22c55e',
                                borderRadius: 2,
                                color: '#22c55e',
                                fontSize: 9,
                                fontFamily: 'monospace',
                                textAlign: 'center',
                                fontWeight: 600,
                              }}>
                                CLAIMED
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '16px 12px', color: '#6e7681', fontSize: 10, textAlign: 'center' }}>
                        No order history
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={styles.noSelection}>
              <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#6e7681' }}>
                Select an item to place orders
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ STYLES ============

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  message: {
    padding: '10px 14px',
    fontSize: 12,
    fontFamily: 'monospace',
    border: '1px solid',
    marginBottom: 12,
  },
  content: {
    display: 'flex',
    flex: 1,
    gap: 12,
    minHeight: 0,
  },
  sidebar: {
    width: 200,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(0, 0, 0, 0.2)',
    border: '1px solid #21262d',
    minHeight: 0,
    maxHeight: '100%',
    overflow: 'hidden',
  },
  searchBox: {
    padding: 12,
    borderBottom: '1px solid #21262d',
  },
  searchInput: {
    width: '100%',
    padding: '8px 10px',
    background: '#0d1117',
    border: '1px solid #30363d',
    color: '#e2e8f0',
    fontSize: 12,
    fontFamily: 'monospace',
    outline: 'none',
  },
  itemList: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
    maxHeight: 'calc(100% - 60px)',
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    cursor: 'pointer',
    gap: 8,
  },
  itemIcon: {
    fontSize: 24,
  },
  itemInfo: {
    flex: 1,
    overflow: 'hidden',
  },
  centerPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(0, 0, 0, 0.2)',
    border: '1px solid #21262d',
    padding: 16,
    overflowY: 'auto',
    minHeight: 0,
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
    paddingBottom: 16,
    borderBottom: '1px solid #21262d',
  },
  itemHeaderIcon: {
    fontSize: 48,
  },
  summaryBox: {
    display: 'flex',
    gap: 16,
    marginBottom: 16,
    padding: 12,
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid #21262d',
  },
  summaryRow: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  summaryLabel: {
    fontSize: 9,
    color: '#6e7681',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  summaryValue: {
    fontSize: 14,
    color: '#58a6ff',
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  orderBook: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  orderBookSide: {
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid #21262d',
    padding: 12,
  },
  orderBookTitle: {
    margin: '0 0 12px 0',
    fontSize: 11,
    color: '#6e7681',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  orderBookList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  orderBookRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    fontFamily: 'monospace',
    padding: '4px 8px',
    background: 'rgba(0, 0, 0, 0.2)',
  },
  rightPanel: {
    width: 280,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  placeOrderBox: {
    background: 'rgba(0, 0, 0, 0.2)',
    border: '1px solid #21262d',
    padding: 16,
  },
  sectionTitle: {
    margin: '0 0 12px 0',
    fontSize: 11,
    color: '#6e7681',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  orderTypeButtons: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    marginBottom: 16,
  },
  orderTypeBtn: {
    padding: '8px 16px',
    background: '#21262d',
    border: '1px solid #30363d',
    color: '#8b949e',
    fontSize: 11,
    fontFamily: 'monospace',
    fontWeight: 600,
    cursor: 'pointer',
  },
  orderTypeBtnActive: {
    background: '#1f6feb',
    color: '#fff',
    borderColor: '#1f6feb',
  },
  inputGroup: {
    marginBottom: 12,
  },
  inputLabel: {
    display: 'block',
    fontSize: 9,
    color: '#6e7681',
    fontFamily: 'monospace',
    letterSpacing: 1,
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    background: '#0d1117',
    border: '1px solid #30363d',
    color: '#e2e8f0',
    fontSize: 14,
    fontFamily: 'monospace',
    outline: 'none',
  },
  totalBox: {
    marginBottom: 16,
    padding: 12,
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid #30363d',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  totalLabel: {
    fontSize: 10,
    color: '#6e7681',
    fontFamily: 'monospace',
    letterSpacing: 1,
  },
  totalValue: {
    fontSize: 16,
    color: '#fbbf24',
    fontFamily: 'monospace',
    fontWeight: 700,
  },
  placeOrderBtn: {
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    color: '#fff',
    fontSize: 12,
    fontFamily: 'monospace',
    fontWeight: 700,
    letterSpacing: 1,
    cursor: 'pointer',
  },
  myOrdersBox: {
    background: 'rgba(0, 0, 0, 0.2)',
    border: '1px solid #21262d',
    padding: 16,
    flex: 1,
    minHeight: 0,
  },
  myOrdersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    overflowY: 'auto',
  },
  myOrderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid #30363d',
  },
  cancelBtn: {
    width: 24,
    height: 24,
    padding: 0,
    background: '#ef4444',
    border: 'none',
    color: '#fff',
    fontSize: 12,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noSelection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
};
